import React, { ReactElement, useEffect, useState } from 'react';
import { Col, Menu, Modal, Row, notification } from 'antd';
import CcxComponentProps from '../../../../core/CcxComponent';
import Service from '../../../../types/Service';
import AppConfirmDialog from '../../../AppConfirmDialog';
import AppGridTable from '../../../ccx/common/AppGridTable';
import CcxIconCloseCircleTwoTone from '../../../ccx/icons/CcxIconCloseCircleTwoTone';
import CcxIconInfoCircleTwoTone from '../../../ccx/icons/CcxIconInfoCircleTwoTone';
import styles from './DataStoreServices.module.less';
import ServiceConnectionInformation from './ServiceConnectionInformation';
import ServiceInformation from './ServiceInformation';
import ServiceStatus from './ServiceStatus';
import { useParams } from 'react-router';
import useDataStoreServices from '../../../../core/hooks/useDataStoreServices';
import LazyLoader from '../../../LazyLoader';
import DeploymentsItem from '../../../../types/DeploymentsItem';
import AppEmpty from '../../../AppEmpty';
import Deployments from '../../../../types/Deployments';
import Helpers from '../../../../services/Helpers';
import { Dropdown } from 'antd';
import { Button } from 'antd';
import CcxContextualMenuIcon from '../../../ccx/icons/CcxContextualMenuIcon';
import ProvisionService from '../../../../services/ProvisionService';
import useDataStoreJobs from '../../../../core/hooks/useDataStoreJobs';
import Job, {
    STATUS_ERROR,
    STATUS_OK,
    STATUS_RUNNING,
    TYPE_ADD,
    TYPE_PROMOTE_REPLICA,
    TYPE_REMOVE,
    TYPE_VOLUME_SIZE,
} from '../../../../types/Job';
import DotLabelStatus from '../../../ccx/common/DotLabelStatus';
import { Tooltip } from 'antd';
import SmallContainer from '../../../ccx/common/SmallContainer';
import InfoIcon from '@severalnines/bar-frontend-components/build/lib/General/InfoIcon';
import PromoteReplicaButton from './PromoteReplicaButton';
import ServiceResources from './ServiceResources';
import AppDeleteModal from '../../../AppDeleteModal';
import ExtendNodeStorageModal from './DataStoreExtendStorageModal';
import CcxSectionHeader from '../../../ccx/common/CcxSectionHeader';
import ScaleNodeModal from './DataStoreScaleNodeModal';

interface UrlProps {
    dataStoreUuid?: string;
}

interface Props extends CcxComponentProps {
    dataStores?: Deployments;
    setDbCount: Function;
    refreshStores: Function;
}

function DataStoreServices({
    dataStores,
    refreshStores,
    setDbCount,
    testId = 'DataStoreServices',
}: Props): ReactElement {
    let { dataStoreUuid } = useParams<UrlProps>();
    const [dataStore, setDataStore] = useState<DeploymentsItem | undefined>(
        undefined
    );
    const { services, refresh, loading, updating } = useDataStoreServices({
        dataStoreUuid,
    });
    const { jobs, refresh: refreshJobs } = useDataStoreJobs({
        dataStoreUuid,
    });
    const [intervalId, setIntervalId] = useState<any>([]);

    const [isButtonDisabled, setIsButtonDisabled] = useState<Boolean>(false);

    const confirmPhrase = 'delete';
    const [dbRows, setDbRows] = useState<any>([]);
    const [lbRows, setLbRows] = useState<any>([]);

    const dbColumns = [
        { title: 'Node information', span: 5 },
        { title: 'Resources', span: 6 },
        { title: 'Role / Permissions', span: 4 },
        { title: 'Connection information', span: 7 },
    ];

    const lbColumns = [
        { title: 'Node information', span: 5 },
        { title: 'Status', span: 3 },
        { title: 'Connection information', span: 15 },
    ];

    const startStoreInterval = () => {
        const id = setInterval(async () => {
            refreshStores && (await refreshStores());
        }, 10000);
        setIntervalId((prevIds: any) => [...prevIds, id]);
    };

    const clearStoreInterval = () => {
        intervalId.forEach((id: any) => clearInterval(id));
    };

    useEffect(() => {
        const AUTO_REFRESH_INTERVAL = 20000;
        const interval = setInterval(async () => {
            refresh && (await refresh());
        }, AUTO_REFRESH_INTERVAL);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const AUTO_REFRESH_INTERVAL = 10000;
        const interval = setInterval(async () => {
            refreshJobs && (await refreshJobs());
        }, AUTO_REFRESH_INTERVAL);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (dataStores && dataStoreUuid) {
            const currentDataStore = dataStores?.getByUuid(dataStoreUuid);
            if (currentDataStore) setDataStore(currentDataStore);
        }
    }, [dataStores, dataStoreUuid]);

    useEffect(() => {
        setDbCount && setDbCount(dbRows.length);
    }, [dbRows]);

    useEffect(() => {
        if (services) {
            const tempDBRows = services.dbServices?.map((s: Service) => {
                function handleMenuOptionClick(e: any) {
                    if (e.key === 'settings') {
                    }
                }

                const onConfirmDelete = () => {
                    return new Promise(async (resolve, reject) => {
                        if (s) {
                            Modal.destroyAll();

                            try {
                                dataStoreUuid &&
                                    (await ProvisionService.removeNode(
                                        dataStoreUuid,
                                        s.getServiceUuid()
                                    ));

                                notification.open({
                                    message: 'Delete node',
                                    description:
                                        'The node will be removed soon.',
                                    icon: <CcxIconInfoCircleTwoTone />,
                                });

                                resolve(null);
                            } catch (e) {
                                notification.open({
                                    message: 'Delete node',
                                    description: `There was an error trying to delete the node. ${e}`,
                                    icon: (
                                        <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />
                                    ),
                                });

                                console.error(e);
                                reject();
                            }
                        }
                    }).catch(() => console.log('Oops errors!'));
                };

                const menu = (
                    <Menu onClick={handleMenuOptionClick}>
                        {s.isRoleReplica() ? (
                            <Menu.Item key="promote">
                                <PromoteReplicaButton service={s} />
                            </Menu.Item>
                        ) : null}
                    </Menu>
                );

                return (
                    dataStore && {
                        borderAlert: s.isStatusError(),
                        statusOverlayText: s.getStatus(),
                        columns: [
                            {
                                content: (
                                    <ServiceInformation
                                        dataStore={dataStore}
                                        service={s}
                                        testId={`${testId}${s.getServiceUuid()}ServiceInformation`}
                                    />
                                ),
                                span: 5,
                            },
                            {
                                content: (
                                    <ServiceResources
                                        service={s}
                                        dataStore={dataStore}
                                    />
                                ),
                                span: 6,
                            },
                            {
                                content: (
                                    <div>
                                        {`${Helpers.capitalizeFirstLetter(
                                            s.getRoleName()
                                        )} / ${Helpers.capitalizeFirstLetter(
                                            s.getPermisiion()
                                        )}`}
                                    </div>
                                ),
                                span: 4,
                            },
                            {
                                content: (
                                    <ServiceConnectionInformation
                                        dataStore={dataStore}
                                        service={s}
                                        testId={`${testId}${s.getServiceUuid()}ServiceConnectionInformation`}
                                    />
                                ),
                                span: 7,
                            },
                            {
                                align: 'center',
                                content: (
                                    <Dropdown
                                        className={
                                            styles.DataStoresDropdownMenu
                                        }
                                        data-testid={`${testId}${s.getServiceUuid()}Menu`}
                                        overlay={menu}
                                        placement="bottomRight"
                                        trigger={['click']}
                                        disabled={
                                            (s.isRolePrimary() &&
                                                dataStore?.isSafespring()) ||
                                            ([
                                                'Replication',
                                                'Postgres_streaming',
                                            ].includes(
                                                dataStore?.getClusterType()
                                            ) &&
                                                s.isRolePrimary()) ||
                                            dataStore?.getClusterType() ===
                                                'Galera' ||
                                            (s.isRolePrimary() &&
                                                dataStore?.isRedis()) ||
                                            filteredJob?.length > 0
                                        }
                                    >
                                        <Button
                                            data-testid={`${testId}${s.getServiceUuid()}MenuButton`}
                                            icon={
                                                <CcxContextualMenuIcon
                                                    testId={`${testId}${s.getServiceUuid()}MenuButtonIcon`}
                                                />
                                            }
                                        />
                                    </Dropdown>
                                ),
                                span: 2,
                            },
                        ],
                        disabled: s.isStarting(),
                    }
                );
            });

            if (jobs) {
                const runningJobs = jobs.jobs.filter(
                    (job: Job) => job.isStatusRunning() && job.isAddNode()
                );

                const statusComponent = (
                    <Tooltip
                        placement="bottom"
                        title={'Deployment is running and will be ready soon'}
                    >
                        <DotLabelStatus
                            type="info"
                            label={'Deploying...'}
                            glow={updating}
                            testId={`${testId}StatusComponent`}
                        />
                    </Tooltip>
                );

                const runningRows = runningJobs.map((job: Job) => {
                    return (
                        dataStore && {
                            borderAlert: false,
                            statusOverlayText: '',
                            columns: [
                                {
                                    content: (
                                        <ServiceInformation
                                            dataStore={dataStore}
                                            service={services.dbServices[0]}
                                            deploying={true}
                                        />
                                    ),
                                    span: 5,
                                },
                                {
                                    content: (
                                        <SmallContainer
                                            testId={testId}
                                            topChildren={statusComponent}
                                        />
                                    ),
                                    span: 3,
                                },
                                {
                                    content: <LazyLoader type="row" rows={1} />,
                                    span: 4,
                                },
                                {
                                    content: <LazyLoader type="row" rows={1} />,
                                    span: 10,
                                },
                                {
                                    align: 'center',
                                    content: (
                                        <Dropdown
                                            className={
                                                styles.DataStoresDropdownMenu
                                            }
                                            data-testid={`${testId}${services.dbServices[0].getServiceUuid()}Menu`}
                                            overlay={null as any}
                                            placement="bottomRight"
                                            trigger={['click']}
                                        >
                                            <Button
                                                data-testid={`${testId}${services.dbServices[0].getServiceUuid()}MenuButton`}
                                                icon={
                                                    <CcxContextualMenuIcon
                                                        testId={`${testId}${services.dbServices[0].getServiceUuid()}MenuButtonIcon`}
                                                    />
                                                }
                                            />
                                        </Dropdown>
                                    ),
                                    span: 2,
                                },
                            ],
                            disabled: true,
                        }
                    );
                });
                setDbRows([...runningRows, ...tempDBRows]);
            } else {
                setDbRows(tempDBRows);
            }

            setLbRows(
                services.lbServices?.map((s: Service) => {
                    function handleMenuOptionClick(e: any) {
                        if (e.key === 'settings') {
                        }
                    }

                    const onConfirmDelete = () => {
                        return new Promise(async (resolve, reject) => {
                            if (s) {
                                Modal.destroyAll();

                                try {
                                    notification.open({
                                        message: 'Delete node',
                                        description:
                                            'The node will be removed soon.',
                                        icon: <CcxIconInfoCircleTwoTone />,
                                    });

                                    resolve(null);
                                } catch (e) {
                                    notification.open({
                                        message: 'Delete node',
                                        description: `There was an error trying to delete the node. ${e}`,
                                        icon: (
                                            <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />
                                        ),
                                    });

                                    console.error(e);
                                    reject();
                                }
                            }
                        }).catch(() => console.log('Oops errors!'));
                    };

                    const menu = (
                        <Menu onClick={handleMenuOptionClick}>
                            <Menu.Item key="settings">Settings</Menu.Item>
                            <Menu.Item key="delete">
                                <AppConfirmDialog
                                    critical={true}
                                    label="Delete"
                                    onOk={onConfirmDelete}
                                    content="The database and load balancers will be completely removed. Database users and data will be lost. This process cannot be reverted."
                                    confirmPhrase={confirmPhrase}
                                    confirmHintText={`To confirm this operation please enter the word "${confirmPhrase}" below:`}
                                    confirmPlaceholder={`Enter the word "${confirmPhrase}"`}
                                />
                            </Menu.Item>
                        </Menu>
                    );

                    return (
                        dataStore && {
                            borderAlert: s.isStatusError(),
                            statusOverlayText: s.getStatus(),
                            columns: [
                                {
                                    content: (
                                        <ServiceInformation
                                            dataStore={dataStore}
                                            service={s}
                                            type="lb"
                                            testId={`${testId}${s.getServiceUuid()}ServiceInformation`}
                                        />
                                    ),
                                    span: 5,
                                },
                                {
                                    content: (
                                        <ServiceStatus
                                            service={s}
                                            testId={`${testId}${s.getServiceUuid()}ServiceStatus`}
                                            updating={false}
                                        />
                                    ),
                                    span: 3,
                                },
                                {
                                    content: (
                                        <ServiceConnectionInformation
                                            dataStore={dataStore}
                                            service={s}
                                            type="lb"
                                            testId={`${testId}${s.getServiceUuid()}ServiceConnectionInformation`}
                                        />
                                    ),
                                    span: 16,
                                },
                            ],
                            disabled: s.isStarting(),
                        }
                    );
                })
            );
        }
    }, [services, updating, jobs]);

    const filteredJob = jobs?.jobs?.filter(
        (job: any) =>
            !job.status.includes(STATUS_ERROR) &&
            [
                TYPE_ADD,
                TYPE_REMOVE,
                TYPE_VOLUME_SIZE,
                TYPE_PROMOTE_REPLICA,
            ].includes(job.type)
    );

    const getJobStatus = (status: string) => {
        if (status === STATUS_OK) {
            return (
                <DotLabelStatus type="info" label={'Created'} glow={false} />
            );
        } else if (status === STATUS_ERROR) {
            return <DotLabelStatus type="error" label={'Error'} glow={false} />;
        } else if (status === STATUS_RUNNING) {
            return <DotLabelStatus type="ok" label={'Running'} glow={false} />;
        }
    };
    const getLastHourFailedJobs = () => {
        const oneHourAgo = new Date();
        oneHourAgo.setHours(oneHourAgo.getHours() - 1);

        const failedJobs: any = {};
        const runningJobs: any = {};

        jobs?.jobs?.forEach((j: any) => {
            if (
                [
                    TYPE_ADD,
                    TYPE_REMOVE,
                    TYPE_VOLUME_SIZE,
                    TYPE_PROMOTE_REPLICA,
                ].includes(j.type) &&
                new Date(j.created_at) >= oneHourAgo
            ) {
                if (j.status === STATUS_ERROR) {
                    if (
                        !failedJobs[j.type] ||
                        new Date(failedJobs[j.type].created_at) <
                            new Date(j.created_at)
                    ) {
                        failedJobs[j.type] = j;
                    }
                } else if (j.status === STATUS_RUNNING) {
                    if (!runningJobs[j.type]) {
                        runningJobs[j.type] = j;
                    }
                }
            }
        });

        return {
            failedJobs: Object.values(failedJobs),
            runningJobs: Object.values(runningJobs),
        };
    };

    const { failedJobs, runningJobs } = getLastHourFailedJobs();

    const jobStatusComponent = (
        <Row gutter={[16, 16]}>
            {runningJobs?.map((j: any) => {
                return (
                    <Col span={6}>
                        <div className={styles.DataStoreServiceJobCard}>
                            <div className={styles.DataStoreServiceJobCardType}>
                                {j.type === TYPE_VOLUME_SIZE
                                    ? 'Scaling storage...'
                                    : j.type === TYPE_PROMOTE_REPLICA
                                    ? 'Promoting replica...'
                                    : 'Scaling nodes...'}
                            </div>
                            <div>{`Created at: ${j.created_at}`}</div>
                            <div className={styles.DataStoreServicesJobStatus}>
                                <span style={{ marginRight: '1rem' }}>
                                    Status:{' '}
                                </span>
                                {getJobStatus(j.status)}
                            </div>
                        </div>
                    </Col>
                );
            })}
            {failedJobs?.map((j: any) => {
                return (
                    <Col span={6}>
                        <div className={styles.DataStoreServiceJobCard}>
                            <div className={styles.DataStoreServiceJobCardType}>
                                {j.type === TYPE_VOLUME_SIZE
                                    ? 'Scaling storage...'
                                    : j.type === TYPE_PROMOTE_REPLICA
                                    ? 'Promoting replica...'
                                    : 'Scaling nodes...'}
                            </div>
                            <div>{`Created at: ${j.created_at}`}</div>
                            <div className={styles.DataStoreServicesJobStatus}>
                                <span style={{ marginRight: '1rem' }}>
                                    Status:{' '}
                                </span>
                                {getJobStatus(j.status)}
                            </div>
                        </div>
                    </Col>
                );
            })}
        </Row>
    );

    return (
        <div className={styles.DataStoreServices} data-testid={testId}>
            <header>{jobStatusComponent}</header>
            <main>
                <CcxSectionHeader>
                    <>
                        {dataStore && !dataStore?.isMSSQL() && (
                            <ScaleNodeModal
                                datastore={dataStore}
                                services={services}
                                job={filteredJob}
                                refresh={refresh}
                                clearStoreInterval={clearStoreInterval}
                                startStoreInterval={startStoreInterval}
                                isButtonDisabled={isButtonDisabled}
                                setIsButtonDisabled={setIsButtonDisabled}
                            />
                        )}
                        {dataStore?.volumeType !== '' &&
                            !dataStore?.isSafespring() && (
                                <ExtendNodeStorageModal
                                    datastore={dataStore!}
                                    job={filteredJob}
                                    refresh={refresh}
                                    clearStoreInterval={clearStoreInterval}
                                    startStoreInterval={startStoreInterval}
                                    isButtonDisabled={isButtonDisabled}
                                    setIsButtonDisabled={setIsButtonDisabled}
                                />
                            )}
                    </>
                </CcxSectionHeader>
                <h4>Nodes</h4>
                {loading && <LazyLoader type="row" />}
                {dbRows?.length > 0 ? (
                    <AppGridTable
                        columns={dbColumns}
                        rows={dbRows}
                        testId={`${testId}Databases`}
                    />
                ) : (
                    !loading && (
                        <AppEmpty message="Databases information is not available for this datastore." />
                    )
                )}

                {!dataStore?.isRedis() && lbRows?.length > 0 && (
                    <>
                        <h4>Load balancers</h4>
                        {loading && <LazyLoader type="row" />}
                        <AppGridTable
                            columns={lbColumns}
                            rows={lbRows}
                            testId={`${testId}LoadBalancers`}
                        />
                    </>
                )}
            </main>
        </div>
    );
}

export default DataStoreServices;
